import React, { useEffect, useRef, useState, useCallback } from 'react';
import { agentService } from '../../services/agentService';
import { Card, CardContent, CardHeader, CardTitle } from '../UI/Card';

interface TeamAgent {
  id: string;
  name: string;
  aiName?: string;
  campaignName?: string;
  status: 'available' | 'ringing' | 'in-call' | 'wrap-up' | 'offline';
  leadName?: string | null;
  companyName?: string | null;
  hangupBy?: 'agent' | 'lead' | null;
}

const statusConfig: Record<
  Exclude<TeamAgent['status'], 'offline'>,
  { dot: string; label: string; text: string }
> = {
  available: { dot: 'bg-green-500', label: 'Available', text: 'text-green-600 dark:text-green-400' },
  ringing: { dot: 'bg-blue-500 animate-pulse', label: 'Ringing', text: 'text-blue-600 dark:text-blue-400' },
  'in-call': { dot: 'bg-amber-500 animate-pulse', label: 'On Call', text: 'text-amber-600 dark:text-amber-400' },
  'wrap-up': { dot: 'bg-red-500', label: 'Call Ended', text: 'text-red-600 dark:text-red-400' },
};

const TeamStatusPanel: React.FC = () => {
  const [agents, setAgents] = useState<TeamAgent[]>([]);
  const [selfId, setSelfId] = useState<string | null>(null);
  const [connected, setConnected] = useState(false);
  const eventSourceRef = useRef<EventSource | null>(null);
  const reconnectTimerRef = useRef<number | undefined>();
  const closedRef = useRef(false);
  const prevStatusesRef = useRef<Record<string, string>>({});

  const connectStream = useCallback(() => {
    if (closedRef.current) return;
    if (typeof window === 'undefined' || !('EventSource' in window)) return;

    const url = agentService.getTeamStatusStreamUrl();
    const es = new EventSource(url);
    eventSourceRef.current = es;

    es.addEventListener('team-status', (event: MessageEvent) => {
      try {
        const data = JSON.parse(event.data) as { agents: TeamAgent[]; selfId: string };
        setAgents(data.agents);
        setSelfId(data.selfId);

        // Check for status transitions to show notifications
        const prevStatuses = prevStatusesRef.current;
        for (const agent of data.agents) {
          if (agent.id === data.selfId) continue; // skip self
          const prev = prevStatuses[agent.id];
          if (prev && prev !== agent.status) {
            if (agent.status === 'in-call' && prev !== 'in-call') {
              // Show a brief toast-style notification via a custom event
              window.dispatchEvent(
                new CustomEvent('team-call-event', {
                  detail: { agentName: agent.aiName || agent.name, type: 'took-call' },
                })
              );
            }
          }
        }
        // Update previous statuses
        const newMap: Record<string, string> = {};
        for (const a of data.agents) newMap[a.id] = a.status;
        prevStatusesRef.current = newMap;
      } catch {
        // ignore parse errors
      }
    });

    es.addEventListener('ping', () => {
      // keepalive
    });

    es.onopen = () => {
      setConnected(true);
    };

    es.onerror = () => {
      if (closedRef.current) return;
      es.close();
      eventSourceRef.current = null;
      setConnected(false);
      // Reconnect with backoff
      if (!reconnectTimerRef.current) {
        reconnectTimerRef.current = window.setTimeout(() => {
          reconnectTimerRef.current = undefined;
          connectStream();
        }, 5000);
      }
    };
  }, []);

  useEffect(() => {
    closedRef.current = false;
    connectStream();

    const handleVisibility = () => {
      if (closedRef.current) return;
      if (document.visibilityState === 'visible' && !eventSourceRef.current) {
        connectStream();
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      closedRef.current = true;
      document.removeEventListener('visibilitychange', handleVisibility);
      eventSourceRef.current?.close();
      eventSourceRef.current = null;
      if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
    };
  }, [connectStream]);

  // Don't render if no team (solo agent or not yet loaded)
  if (agents.length <= 1 && connected) return null;
  if (agents.length === 0) return null;

  // Sort: self first, then by status priority (in-call, ringing, available, idle), then by name
  const statusPriority: Record<string, number> = { 'in-call': 0, ringing: 1, 'wrap-up': 2, available: 3, idle: 4 };
  const sorted = [...agents].sort((a, b) => {
    if (a.id === selfId) return -1;
    if (b.id === selfId) return 1;
    const pa = statusPriority[a.status] ?? 4;
    const pb = statusPriority[b.status] ?? 4;
    if (pa !== pb) return pa - pb;
    const an = a.aiName || a.name;
    const bn = b.aiName || b.name;
    return an.localeCompare(bn);
  });

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm">Team Status</CardTitle>
          <span className="text-xs text-muted-foreground">
            {agents.filter((a) => a.status !== 'offline').length} online · {agents.length} agents
          </span>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="space-y-1.5">
          {sorted.map((agent) => {
            const displayName = agent.aiName || agent.name;
            const isSelf = agent.id === selfId;
            const cfg =
              agent.status === 'offline'
                ? { dot: 'bg-muted-foreground/40', label: 'Offline', text: 'text-muted-foreground' }
                : statusConfig[agent.status];
            return (
              <div
                key={agent.id}
                className="flex items-center justify-between py-1.5 px-2 rounded-md hover:bg-accent/50 transition-colors"
              >
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  <span
                    className={`inline-block h-2.5 w-2.5 rounded-full flex-shrink-0 ${
                      cfg?.dot ?? 'bg-muted-foreground/40'
                    }`}
                  />
                  <div className="min-w-0 flex-1">
                    <span className="text-sm truncate text-foreground block">
                      {displayName}
                      {isSelf && (
                        <span className="text-xs text-muted-foreground ml-1">(you)</span>
                      )}
                    </span>
                    <span className="text-xs text-muted-foreground truncate block">
                      {!isSelf &&
                      (agent.status === 'in-call' || agent.status === 'ringing' || agent.status === 'wrap-up') &&
                      agent.leadName
                        ? `${agent.leadName}${agent.companyName ? ` · ${agent.companyName}` : ''}`
                        : agent.campaignName || '—'}
                    </span>
                  </div>
                </div>
                <div className="flex flex-col items-end flex-shrink-0">
                  <span className={`text-xs font-medium ${cfg?.text ?? 'text-muted-foreground'}`}>
                    {agent.status === 'wrap-up' && agent.hangupBy
                      ? `Hung up by ${agent.hangupBy}`
                      : cfg?.label ?? 'Offline'}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

export default TeamStatusPanel;
